import type * as vNG from "v-network-graph";
import type { RouteLocationRaw } from "vue-router";
import { Locales } from "./i18n";
import { CARD_DEFAULT_WIDTH } from "./composables/useResizableCardWidth";

export interface AccessTokenType {
  capabilites: number[];
  email: string;
  exp: number;
  iat: number;
  id: number;
  roles: UserRole[];
}
export type HostType = {
  is_unknown?: boolean;
  agent_id: string;
  agent_version: string;
  hostname: string;
  id: string;
  interfaces: HostInterfaceType[];
  info: {
    cpu_cores: number;
    kernel_version: string;
    kernel: string;
    os: string;
    platform: string;
    labels: string[];
  };
  ctime: string;
  mtime: string;
  dead_dttm: string;
  params: string;
  module_states?: string;
  class: string;
  mode: AgentParameterMode;
  state?: AgentStates;
};

export type HostShortInfo = {
  id: string;
  agent_id: string;
  hostname: string;
  interfaces: {
    eth: string;
    host: string;
    id: string;
    ip: string;
    name: string;
    tags: string[];
    isSelected: boolean;
  }[];
};

/** Host status counters from GET /api/v1/hosts/hosts/summary (dashboard). */
export type HostsSummary = {
  active: number;
  monitoring: number;
  enforcing: number;
  dead: number;
  total: number;
};

export type HostInterfaceType = {
  eth: string;
  host: string;
  hostname?: string;
  agent?: string;
  id: string;
  ip: string;
  name: string;
  tags: string[];
};

export type FlowType = {
  id?: string;
  src: string;
  dst: string;
  filter_dst: string;
  filter_src?: string;
  meta?: string;
  isReverse?: boolean;
  recommendation_type?: string | null;
};

export type PatchFlowType = { id: string } & Partial<
  Pick<FlowType, "src" | "dst" | "filter_src" | "filter_dst" | "meta">
>;

export function emptyFlow(): FlowType {
  return {
    src: "",
    dst: "",
    filter_dst: "",
    meta: "",
  };
}

export type FlowFilter = {
  proto: number[];
  ports?: number[][];
};

export type HostGroupType = {
  id?: string;
  interface_uuids: string[];
  meta: string;
};

export interface HostGroupMetaType {
  name: string;
}

export interface ExternalHostMetaType {
  name: string;
}

export type UUIDClass =
  | "UUID_CLASS_UNSPECIFIED"
  | "UUID_CLASS_HOST"
  | "UUID_CLASS_AGENT"
  | "UUID_CLASS_IFACE"
  | "UUID_CLASS_GROUP"
  | "UUID_CLASS_EXTERNAL";
export type BlacklistItemType = {
  class: UUIDClass;
  id?: string;
  item: string;
  meta: string;
};

export type ExternalHostType = {
  cidrs: string[];
  dns: string[];
  id?: string;
  meta: string;
};

export type Mode =
  | "default"
  | "drag"
  | "groups"
  | "rules"
  | "search"
  | "external"
  | "path"
  | "tags"
  | "search_path"
  | "ai";

export enum UserRole {
  Admin = "admin",
  Viewer = "viewer",
  Guest = "guest",
  SegViewer = "segviewer",
  SegAdmin = "segadmin",
}

export interface UserType {
  email: string;
  login: string;
  roles: UserRole[];
  user_id: string;
}

export interface RegisterRequestWithRoles {
  login: string;
  email: string;
  password: string;
  roles: UserRole[];
}
export function emptyRegisterRequestWithRoles(): RegisterRequestWithRoles {
  return {
    login: "",
    email: "",
    password: "",
    roles: [],
  };
}

export interface AuthRequest {
  authType: ProviderType;
  login: string;
  email: string;
  password: string;
}

export function emptyAuthRequest(): AuthRequest {
  return {
    authType: "native",
    login: "",
    email: "",
    password: "",
  };
}

export type ProviderType = "native" | "oidc" | "ldap";

export const providerToNames: Map<ProviderType, string> = new Map([
  ["native", "Segment Secure"],
  ["oidc", "Open ID Connect"],
  ["ldap", "LDAP"],
]);
export const namesToProviders: Map<string, ProviderType> = new Map([
  ["Segment Secure", "native"],
  ["Open ID Connect", "oidc"],
  ["LDAP", "ldap"],
]);

export interface OIDCParams {
  client_id: string;
  client_secret: string;
  auth_url: string;
  redirect_url: string;
  // Only members of this IdP group (from the `groups` claim) may sign in.
  required_group?: string;
  // Group → role, re-applied on every login; see utils/roleMapping.ts.
  role_mapping?: Record<string, string>;
  role_priority?: string[];
}

// export function emptyOIDCParams(): OIDCParams {
//   return {
//     client_id: "",
//     client_secret: "",
//     auth_url: "",
//     redirect_url: document.location.origin + "/auth/callback",
//   };
// }

export interface LDAPParams {
  fqdn: string;
  base_dn: string;
  user_dn: string;
  service_user: string;
  password: string;
  username_attribute: string;
  email_attribute: string;
  // Where to look for groups; the backend defaults to ou=groups,<base_dn>.
  group_dn?: string;
  // Only members of this directory group (cn or full DN) may sign in.
  required_group?: string;
  // Upgrade an ldap:// connection to TLS.
  start_tls?: boolean;
  // PEM of the CA that issued the LDAP server certificate.
  ca_cert?: string;
  // Active Directory: count membership through nested groups.
  nested_groups?: boolean;
  role_mapping?: Record<string, string>;
  role_priority?: string[];
}

// Form state of a new LDAP provider; role mapping is edited separately.
export type LDAPForm = Required<
  Omit<LDAPParams, "role_mapping" | "role_priority">
>;

export function emptyLDAPParams(): LDAPForm {
  return {
    fqdn: "",
    base_dn: "",
    user_dn: "",
    service_user: "",
    password: "",
    username_attribute: "",
    email_attribute: "",
    group_dn: "",
    required_group: "",
    start_tls: false,
    ca_cert: "",
    nested_groups: false,
  };
}

export interface AuthProvider {
  type: ProviderType;
  params: OIDCParams | LDAPParams;
}

export interface SegmentMetaType {
  name: string;
}
export type SegmentType = {
  admins: string[];
  group_uuids: string[];
  id?: string;
  meta: string;
  viewers: string[];
};
export function emptySegment(): SegmentType {
  return {
    meta: '{ name: "" }',
    admins: [],
    viewers: [],
    group_uuids: [""],
  };
}

/**
 * License info returned by `GET /api/v1/hosts/license`.
 * `from`/`until` are RFC3339 date-time strings; `until` is the expiry.
 * `agents` is the seat limit (`0` = unlimited); `agents_count` is current usage.
 */
export type LicenseInfo = {
  org: string;
  email: string;
  from: string;
  until: string;
  agents: number;
  agents_count: number;
};

export type LicenseStatus = "valid" | "expiring_soon" | "expired";

export type EntityType = {
  id: string;
  name: string;
  interfaces: HostInterfaceType[];
  children: {
    hostId: string;
    hostname: string;
    interfaces: { id: string; name: string; ip: string }[];
  }[];
  cidrs: string[];
  dns: string[];
  tags: HostTag[];
  recommendations: RecommendationFlow[];
  class: UUIDClass;
  groups: HostGroupType[][];
  flows: FlowType[];
  module_states?: string;
  dead_dttm?: string;
};

export type RuleActionItems = {
  id: string;
  name: string;
  interfaces?: HostInterfaceType[];
  class: UUIDClass;
  type: "src" | "dst";
};

export type GroupActionsMenuType = {
  id: string;
  name: string;
  interfaces?: HostInterfaceType[];
  class: UUIDClass;
};

export type HostnameInfo = {
  id: string;
  hostname: string;
  class: UUIDClass;
  interfaces: HostInterfaceType[];
};

export interface clusterGroup {
  name: string;
  items: string[];
}

export interface ClusterGroupsWithHosts {
  name: string;
  hosts: {
    id: string;
    agent_id: string;
    hostname: string;
    interfaceIds: string[];
  }[];
}

export interface GroupManagerEntities {
  host: { id: string; hostname: string };
  interfaces: { id: string; ip: string }[];
}

export interface SearchPathEntities {
  node: { id: string; hostname: string; type: "AGENT" | "EXTERNAL" | "GROUP" };
  interface: { id: string; ip: string };
}

export type TreeNodes = Record<string, TreeNode>;
export interface TreeNode extends vNG.Node {
  is_unknown?: boolean;
  id: string;
  name: string;
  class: UUIDClass;
  interfaces?: HostInterfaceType[];
  blacklisted: boolean;
  unwrapped?: string[];
  children?: TreeNodes;
  status_label?: NodeStatusLabel;
  mode?: AgentParameterMode;
  state?: AgentStates;
  rulesCount?: number;
  isHaveUnreadAlerts?: boolean;
  kernel?: string;
  dead_dttm?: string;
}

export type NodeStatusLabel = "warning" | "critical";

export type EdgeTypes = "recommendation" | "rule" | "group_membership";

export interface Edge extends vNG.Edge {
  id?: string;
  dashed: boolean;
  directional: boolean;
  color: string;
  filter_dst: string;
  filter_src?: string;
  label?: string;
  // additional label for -hidden edges
  group_hidden?: boolean;
  type?: EdgeTypes;
  is_biderectional?: boolean;
}
export type Edges = Record<string, Edge>;

export type KprobeEvent = {
  aid: string;
  comm: string;
  desc: string;
  gid: number;
  id: number;
  pid: number;
  time: string;
  uid: number;
};
export type ProcessEvent = {
  aid: string;
  comm: string;
  count: number;
  desc: string;
  ts_max: string;
  ts_min: string;
  username: string;
  ifaces?: string[];
};
export type FirewallType =
  | "FIREWALL_TYPE_UNSPECIFIED"
  | "FIREWALL_TYPE_OUR_FW"
  | "FIREWALL_TYPE_IPTABLES"
  | "FIREWALL_TYPE_NFTABLES";

export const firewallTypeToName: Map<FirewallType, string> = new Map([
  ["FIREWALL_TYPE_UNSPECIFIED", "fw_type.unspecified"],
  ["FIREWALL_TYPE_OUR_FW", "fw_type.our_fw"],
  ["FIREWALL_TYPE_IPTABLES", "fw_type.iptables"],
  ["FIREWALL_TYPE_NFTABLES", "fw_type.nftables"],
]);

export const firewallTypeByName: Map<string, FirewallType> = new Map([
  ["fw_type.unspecified", "FIREWALL_TYPE_UNSPECIFIED"],
  ["fw_type.our_fw", "FIREWALL_TYPE_OUR_FW"],
  ["fw_type.iptables", "FIREWALL_TYPE_IPTABLES"],
  ["fw_type.nftables", "FIREWALL_TYPE_NFTABLES"],
]);

export type FirewallEvent = {
  aid: string;
  ifname: string;
  ts: string;
  ingress: number;
  proto: number;
  src_ip: string;
  src_port: number;
  dst_ip: string;
  dst_port: number;
  reason: number;
  fw_type: FirewallType;
  domain_name: string;
  pid: number;
  cmdline: string;
  username: string;
  count?: number;
  min_ts?: string;
  max_ts?: string;
};

export type FirewallEventsSortBy = "SORT_BY_UNSPECIFIED" | "SORT_BY_PACKETS";

export type FirewallEventsResponse = {
  items: FirewallEvent[];
  total_count: number;
};
export type NetflowEvent = {
  aid: string;
  ifaddr: string;
  ifport: number;
  ifname: string;
  in_bytes: string;
  in_pkts: string;
  out_bytes: string;
  out_pkts: string;
  noifaddr: string;
  noifport: number;
  proto: number;
  domain_name: string;
  ts: string;
};

export type NotificationRule = {
  id?: string;
  level: AlertLevel;
  transport: string;
  targets: string;
};
export enum NotificationTransportType {
  Syslog = "SYSLOG_TYPE",
  Email = "EMAIL_TYPE",
  Telegram = "TELEGRAM_TYPE",
  Webhook = "WEBHOOK_TYPE",
  None = "NONE_TYPE",
}
export type NotificationTransportParams =
  | NotificationSyslogTransportParams
  | NotificationEmailTransportParams
  | NotificationTelegramTransportParams
  | NotificationWebhookTransportParams;
export type NotificationTransport = {
  id?: string;
  config: string;
  configObject?: NotificationTransportParams;
  type: NotificationTransportType;
  meta: string;
};
export const transportToNames: Map<NotificationTransportType, string> = new Map(
  [
    [NotificationTransportType.Syslog, "notifications.transport_type.syslog"],
    [NotificationTransportType.Email, "notifications.transport_type.email"],
    [
      NotificationTransportType.Telegram,
      "notifications.transport_type.telegram",
    ],
    [NotificationTransportType.Webhook, "notifications.transport_type.webhook"],
    [NotificationTransportType.None, "notifications.transport_type.none"],
  ],
);
export const namesToTransport: Map<string, NotificationTransportType> = new Map(
  [
    ["notifications.transport_type.syslog", NotificationTransportType.Syslog],
    ["notifications.transport_type.email", NotificationTransportType.Email],
    [
      "notifications.transport_type.telegram",
      NotificationTransportType.Telegram,
    ],
    ["notifications.transport_type.webhook", NotificationTransportType.Webhook],
    ["notifications.transport_type.none", NotificationTransportType.None],
  ],
);

export type NotificationSyslogTransportParams = {
  host: string;
  port: number;
};
export function emptySyslogTransport(): NotificationSyslogTransportParams {
  return { host: "", port: 514 };
}
export type NotificationSyslogTargetParams = {
  tag: string;
};
export function emptySyslogTarget(): NotificationSyslogTargetParams {
  return { tag: "" };
}

export type NotificationEmailTransportParams = {
  host: string;
  port: number;
  login: string;
  password: string;
};
export function emptyEmailTransport(): NotificationEmailTransportParams {
  return {
    host: "",
    port: 25,
    login: "",
    password: "",
  };
}
export type NotificationEmailTargetParams = {
  receivers: string[];
};
export function emptyEmailTarget(): NotificationEmailTargetParams {
  return { receivers: [""] };
}

export type NotificationTelegramTransportParams = {
  token: string;
};
export function emptyTelegramTransport(): NotificationTelegramTransportParams {
  return { token: "" };
}
export type NotificationTelegramTargetParams = {
  chat_id: string;
};
export function emptyTelegramTarget(): NotificationTelegramTargetParams {
  return { chat_id: "" };
}

export type NotificationWebhookTransportParams = {
  url: string;
};
export function emptyWebhookTransport(): NotificationWebhookTransportParams {
  return { url: "" };
}

export function getTransportAdditionalInfo(transport: NotificationTransport) {
  switch (transport.type) {
    case NotificationTransportType.Email:
      return (transport.configObject as NotificationEmailTransportParams).host;
    case NotificationTransportType.Telegram:
      return (transport.configObject as NotificationTelegramTransportParams)
        .token;
    case NotificationTransportType.Syslog:
      return (transport.configObject as NotificationSyslogTransportParams).host;
    case NotificationTransportType.Webhook:
      return (transport.configObject as NotificationWebhookTransportParams).url;
    default:
      return "Unknown";
  }
}

export function getTargetAdditionalInfo(
  target: string,
  type: NotificationTransportType,
): string {
  if (!target) return "";
  const t = JSON.parse(target);
  switch (type) {
    case NotificationTransportType.Email:
      return (t as NotificationEmailTargetParams).receivers.join(",");
    case NotificationTransportType.Telegram:
      return (t as NotificationTelegramTargetParams).chat_id;
    case NotificationTransportType.Syslog:
      return (t as NotificationSyslogTargetParams).tag;
    case NotificationTransportType.Webhook:
      return "";
    default:
      return "Unknown";
  }
}

export type operationLogTypes = "create" | "delete" | "update";

export const operationLogTypeToText: Map<operationLogTypes, string> = new Map([
  ["create", "rules_log.operation_type.created"],
  ["delete", "rules_log.operation_type.deleted"],
  ["update", "rules_log.operation_type.updated"],
]);
export const operationLogTypeByText: Map<string, operationLogTypes> = new Map([
  ["rules_log.operation_type.created", "create"],
  ["rules_log.operation_type.deleted", "delete"],
  ["rules_log.operation_type.updated", "update"],
]);

export type OperationLogFlowUpdateData = {
  old: FlowType;
  new: FlowType;
};

export type ModuleState =
  | "AGENT_MODULE_IPTABLES"
  | "AGENT_MODULE_NFTABLES"
  | "AGENT_MODULE_KPROBE"
  | "AGENT_MODULE_FIM"
  | "AGENT_MODULE_FIREWALL"
  | "AGENT_MODULE_NETFLOW";

export const MODULE_STATES_VARIABLES = {
  AGENT_MODULE_IPTABLES: 1,
  AGENT_MODULE_NFTABLES: 2,
  AGENT_MODULE_KPROBE: 4,
  AGENT_MODULE_FIM: 8,
  AGENT_MODULE_FIREWALL: 16,
  AGENT_MODULE_NETFLOW: 32,
} as const;

export type ModuleStates = typeof MODULE_STATES_VARIABLES;

export const moduleStatesByName: Map<string, ModuleState> = new Map([
  ["agent_menu.module_states.iptables", "AGENT_MODULE_IPTABLES"],
  ["agent_menu.module_states.nftables", "AGENT_MODULE_NFTABLES"],
  ["agent_menu.module_states.kprobe", "AGENT_MODULE_KPROBE"],
  ["agent_menu.module_states.fim", "AGENT_MODULE_FIM"],
  ["agent_menu.module_states.firewall", "AGENT_MODULE_FIREWALL"],
  ["agent_menu.module_states.netflow", "AGENT_MODULE_NETFLOW"],
]);

export const moduleStatesToName: Map<ModuleState, string> = new Map([
  ["AGENT_MODULE_IPTABLES", "agent_menu.module_states.iptables"],
  ["AGENT_MODULE_NFTABLES", "agent_menu.module_states.nftables"],
  ["AGENT_MODULE_KPROBE", "agent_menu.module_states.kprobe"],
  ["AGENT_MODULE_FIM", "agent_menu.module_states.fim"],
  ["AGENT_MODULE_FIREWALL", "agent_menu.module_states.firewall"],
  ["AGENT_MODULE_NETFLOW", "agent_menu.module_states.netflow"],
]);

export type AlertClass =
  | "ALERT_CLASS_HOST_DEAD"
  | "ALERT_CLASS_FIREWALL"
  | "ALERT_CLASS_KPROBE"
  | "ALERT_CLASS_AGENT_MODE_ENFORCING"
  | "ALERT_CLASS_UNSPECIFIED"
  | "ALERT_CLASS_HOST_UNTRUSTED"
  | "ALERT_CLASS_NEW_UNKNOWN_DNS_SERVERS";
export const alertClassByName: Map<string, AlertClass> = new Map([
  ["notifications.class.host_dead", "ALERT_CLASS_HOST_DEAD"],
  ["notifications.class.firewall", "ALERT_CLASS_FIREWALL"],
  ["notifications.class.process", "ALERT_CLASS_KPROBE"],
  ["notifications.class.enforcing_mode", "ALERT_CLASS_AGENT_MODE_ENFORCING"],
  ["notifications.class.unknown_dns", "ALERT_CLASS_NEW_UNKNOWN_DNS_SERVERS"],
  ["notifications.class.untrusted", "ALERT_CLASS_HOST_UNTRUSTED"],
  ["notifications.class.unspecified", "ALERT_CLASS_UNSPECIFIED"],
]);

export const alertClassToName: Map<AlertClass, string> = new Map([
  ["ALERT_CLASS_HOST_UNTRUSTED", "notifications.class.untrusted"],
  ["ALERT_CLASS_UNSPECIFIED", "notifications.class.unspecified"],
  ["ALERT_CLASS_HOST_DEAD", "notifications.class.host_dead"],
  ["ALERT_CLASS_FIREWALL", "notifications.class.firewall"],
  ["ALERT_CLASS_KPROBE", "notifications.class.process"],
  ["ALERT_CLASS_AGENT_MODE_ENFORCING", "notifications.class.enforcing_mode"],
  ["ALERT_CLASS_NEW_UNKNOWN_DNS_SERVERS", "notifications.class.unknown_dns"],
]);
export const alertPriority: Record<AlertLevel, number> = {
  ALERT_LEVEL_CRITICAL: 3,
  ALERT_LEVEL_WARNING: 2,
  ALERT_LEVEL_INFO: 1,
  ALERT_LEVEL_UNSPECIFIED: 0,
};
export type AlertLevel =
  | "ALERT_LEVEL_CRITICAL"
  | "ALERT_LEVEL_WARNING"
  | "ALERT_LEVEL_INFO"
  | "ALERT_LEVEL_UNSPECIFIED";
export const alertLevelByName: Map<string, AlertLevel> = new Map([
  ["notifications.level.critical", "ALERT_LEVEL_CRITICAL"],
  ["notifications.level.warning", "ALERT_LEVEL_WARNING"],
  ["notifications.level.info", "ALERT_LEVEL_INFO"],
  ["notifications.level.unspecified", "ALERT_LEVEL_UNSPECIFIED"],
]);
export const alertLevelToName: Map<AlertLevel, string> = new Map([
  ["ALERT_LEVEL_CRITICAL", "notifications.level.critical"],
  ["ALERT_LEVEL_WARNING", "notifications.level.warning"],
  ["ALERT_LEVEL_INFO", "notifications.level.info"],
  ["ALERT_LEVEL_UNSPECIFIED", "notifications.level.unspecified"],
]);

export type FirewallEventsFetchOptions = {
  agentId?: string;
  limit?: number;
  offset?: number;
  fromDttm?: string;
  toDttm?: string;
  comm?: string;
  fwType?: FirewallType;
  hostSearch?: string;
  port?: number;
  username?: string;
  compact?: string;
  sortBy?: FirewallEventsSortBy;
  packetsThreshold?: number;
  alertClass?: AlertClass;
  alertLevel?: AlertLevel;
  alertDescription?: string;
  onlyAffected?: boolean;
  attachment?: string;
  onlyNotMarkedAsRead?: boolean;
};

export type Alert = {
  affected_users: string[];
  agent_id: string;
  class: AlertClass;
  description: string;
  dttm: string;
  extra: string;
  id: string;
  level: AlertLevel;
  marked_as_read?: boolean;
};

export type FlowTemplate = {
  flows: { items: FlowType[] };
  id?: string;
  meta: string;
  segment?: string;
};

export type OplogToolItem = {
  oplog_id: number;
  dttm?: string;
  user_login?: string;
  user_email?: string;
  op: "create" | "delete";
  flow: {
    flow_id: number;
    src_id: string;
    dst_id: string;
    src_name?: string;
    dst_name?: string;
    filter_src?: string;
    filter_dst?: string;
  };
  unknown?: boolean;
};

export function unknownOplogItem(id: number): OplogToolItem {
  return {
    oplog_id: id,
    op: "create",
    flow: { flow_id: 0, src_id: "", dst_id: "" },
    unknown: true,
  };
}

export type AgentParameterMode =
  | "AGENT_MODE_UNKNOWN"
  | "AGENT_MODE_DISCOVERY"
  | "AGENT_MODE_ENFORCING";
export type AgentParameters = {
  mode: AgentParameterMode;
};

export type AgentStates =
  | "AGENT_STATE_UNKNOWN"
  | "AGENT_STATE_RUNNING"
  | "AGENT_STATE_REQUESTED_OFF"
  | "AGENT_STATE_NOTIFIED_OFF"
  | "AGENT_STATE_DEAD";
export type AgentState = {
  state: AgentStates;
};

export type ConversationItem = {
  src_agent: string;
  src_ip: string;
  src_port: number;
  src_cmdline: string;
  src_username: string;
  src_domain_name: string;
  src_ifname?: string;

  fw_pkts: string;
  fw_bytes: string;

  service_port: number;
  service_port_is_src: boolean;

  proto: number;

  bk_pkts: string;
  bk_bytes: string;

  first_ts?: string;
  last_ts?: string;

  dst_agent: string;
  dst_ip: string;
  dst_domain_name: string;
  dst_port: number;
  dst_cmdline: string;
  dst_username: string;
  dst_ifname?: string;
  meta?: string;
};

const LayoutAlgorithms = {
  "tight-tree": null,
  "longest-path": null,
} as const;
export type LayoutAlgorithm = keyof typeof LayoutAlgorithms;
export const layoutAlgorithmList: LayoutAlgorithm[] = Object.keys(
  LayoutAlgorithms,
) as LayoutAlgorithm[];

export const GRAPH_EDGE_DISTANCE_MIN = 1;
export const GRAPH_EDGE_DISTANCE_MAX = 500;
export const GRAPH_EDGE_DISTANCE_DEFAULT = 100;

export type GraphSettingsType = {
  enableForces: boolean;
  edgeDistance: number;
  rankerType: LayoutAlgorithm;
  showGroupLinks: boolean;
  showLinkRecommendationsHost: boolean;
  showLinkRecommendationsGroups: boolean;
  showGrid: boolean;
  showHostInterfaces: boolean;
  /** Rule (solid, non-dashed) edges. Defaults true. */
  showRuleEdges: boolean;
  /** Recommendation arcs. Loaders keep this true so a saved snapshot cannot straighten them. */
  curvedEdges: boolean;
  /** Library zoom-on-dblclick. Defaults true; the map turns it off so host
   * dblclick can recenter. */
  doubleClickZoomEnabled: boolean;
  /** Radial focus view: search/click rebuilds the map around one node
   * instead of only flying the camera there. Off until the toolbar toggle. */
  focusViewEnabled: boolean;
};
export function defaultGraphSettings(): GraphSettingsType {
  return {
    enableForces: false,
    edgeDistance: GRAPH_EDGE_DISTANCE_DEFAULT,
    rankerType: "tight-tree",
    showGroupLinks: true,
    showLinkRecommendationsHost: false,
    showLinkRecommendationsGroups: false,
    showGrid: false,
    showHostInterfaces: false,
    showRuleEdges: true,
    curvedEdges: true,
    doubleClickZoomEnabled: true,
    focusViewEnabled: false,
  };
}

export type FoldedMenus = {
  settings?: boolean;
  connected: boolean;
  conversations: boolean;
  firewall: boolean;
  tags: boolean;
  events: boolean;
  ips: boolean;
  domains: boolean;
  children: boolean;
  rules: boolean;
  interfaces: boolean;
  groups: boolean;
  linkedGroups: boolean;
  alerts: boolean;
  trafficControl: boolean;
};

export function defaultFoldedMenus(): FoldedMenus {
  return {
    connected: false,
    conversations: false,
    firewall: false,
    tags: false,
    events: false,
    ips: false,
    domains: false,
    children: false,
    rules: false,
    interfaces: false,
    groups: false,
    linkedGroups: false,
    alerts: false,
    settings: false,
    trafficControl: true,
  };
}

export type GenericRowType = {
  [key: string]: string | string[] | number | UUIDClass | object | undefined;
  id: string;
};

export type ColumnType<T> = {
  label: string;
  name: T;
  ratio?: number;
  sortable?: boolean;
  withSlot?: string;
  needsTranslation?: boolean;
  linkTo?: string;
  linkQuery?: Record<string, string>;
  hoverable?: boolean;
  dynamicStyle?: (row: string) => string;
};

export type TabType = {
  link: string;
  name: string;
  icon?: string;
  iconClass?: string;
};

export type DropdownEntry = { id: string; name: string };

export type LayoutInfo = {
  layouts: vNG.NodePositions;
  collapsed_layouts: vNG.NodePositions;
  group_unwraps: Record<string, string[]>;
  viewBox?: vNG.Box;
  dttm: Date;
};

export function emptyLayoutInfo(): LayoutInfo {
  return {
    layouts: {},
    collapsed_layouts: {},
    group_unwraps: {},
    dttm: new Date(),
  };
}

export type ExcludeType = {
  aid: string;
  event: string;
  hash: string;
  id: string;
};

export type FirewallExclude = {
  aid: string;
  domain_name: string;
  src_ip: string;
  src_port: number;
  dst_ip: string;
  dst_port: number;
  ifname: string;
  ingress: number;
  proto: number;
  reason: number;
  // ts: string;
};

export function emptyFirewallExclude(): FirewallExclude {
  return {
    aid: "",
    domain_name: "",
    dst_ip: "",
    dst_port: 0,
    ifname: "",
    ingress: 0,
    proto: 0,
    reason: 0,
    src_ip: "",
    src_port: 0,
    // ts: new Date().toISOString()
  };
}

export type UserParameters = {
  layout_info: LayoutInfo;
  graph_settings: GraphSettingsType;
  folded_menu: FoldedMenus;
  locale: Locales;
  is_tutorial_passed: boolean;
  card_width: number;
  network_map_filters: NetworkMapFiltersType;
  /**
   * Показывать ли группу «нераспределённые узлы» (в коде — pseudo_group, см.
   * composables/usePseudoGroup.ts). При `false` хосты вне групп рисуются
   * отдельными узлами.
   */
  show_unallocated_nodes: boolean;
};

export function emptyNetworkMapFilters(): NetworkMapFiltersType {
  return {};
}

export function emptyUserParameters(): UserParameters {
  return {
    layout_info: emptyLayoutInfo(),
    graph_settings: defaultGraphSettings(),
    folded_menu: defaultFoldedMenus(),
    locale: "ru",
    is_tutorial_passed: false,
    card_width: CARD_DEFAULT_WIDTH,
    network_map_filters: emptyNetworkMapFilters(),
    // getUserParameters подмешивает эти дефолты к сохранённой meta, поэтому
    // существующие пользователи без поля получают прежнее поведение.
    show_unallocated_nodes: true,
  };
}

export type HostTag = {
  id: string;
  color: string;
};

export const NO_TAG_FILTER_ID = "__no_tag__";

export const tagColors = [
  { id: "red", name: "agent_menu.tag_colors.red", color: "red" },
  { id: "yellow", name: "agent_menu.tag_colors.yellow", color: "yellow" },
  { id: "green", name: "agent_menu.tag_colors.green", color: "green" },
  { id: "blue", name: "agent_menu.tag_colors.blue", color: "blue" },
  { id: "white", name: "agent_menu.tag_colors.white", color: "white" },
  { id: "orange", name: "agent_menu.tag_colors.orange", color: "orange" },
  { id: "violet", name: "agent_menu.tag_colors.violet", color: "violet" },
  { id: "indigo", name: "agent_menu.tag_colors.indigo", color: "indigo" },
];

export type AlertFirewallDescription = {
  aid: string;
  ifname: string;
  proto: number;
  count: number;
  time_begin: string;
  time_end: string;
  src_ip: string;
  dst_ip: string;
  domain_name: string;
  username: string;
  pid: number;
};

export const RecommendationStatus = {
  Unknown: 0,
  Accepted: 1,
  Rejected: -1,
} as const;

export type RecommendationStatusType =
  (typeof RecommendationStatus)[keyof typeof RecommendationStatus];

export type RecommendationSource =
  | "FLOW_RECOMMENDATION_SOURCE_UNKNOWN"
  | "FLOW_RECOMMENDATION_SOURCE_CONVERSATION"
  | "FLOW_RECOMMENDATION_SOURCE_FIREWALL";

export type RecommendationTypeFilter = Extract<
  RecommendationSource,
  | "FLOW_RECOMMENDATION_SOURCE_CONVERSATION"
  | "FLOW_RECOMMENDATION_SOURCE_FIREWALL"
>;

export function isRecommendationTypeFilter(
  value: unknown,
): value is RecommendationTypeFilter {
  return (
    value === "FLOW_RECOMMENDATION_SOURCE_CONVERSATION" ||
    value === "FLOW_RECOMMENDATION_SOURCE_FIREWALL"
  );
}

export const recommendationTypeToEntryKey: Map<
  RecommendationTypeFilter,
  string
> = new Map([
  [
    "FLOW_RECOMMENDATION_SOURCE_CONVERSATION",
    "tables.recommendation_type_conversation",
  ],
  [
    "FLOW_RECOMMENDATION_SOURCE_FIREWALL",
    "tables.recommendation_type_firewall",
  ],
]);

export const recommendationTypeByEntryKey: Map<
  string,
  RecommendationTypeFilter
> = new Map(
  [...recommendationTypeToEntryKey.entries()].map(([type, key]) => [key, type]),
);

export type RecommendationFlow = {
  id: string;
  flow: FlowType;
  // Recommendation status: 0 - unknown, 1 - accepted, -1 - rejected
  status: RecommendationStatusType;
  source: RecommendationSource;
  total_packets?: string;
  total_bytes?: string;
  connections_count?: string;
  src_frequency?: number;
  dst_frequency?: number;
};

export type ListRecommendationsParams = {
  with_rejected?: boolean;
  only_pending?: boolean;
  source?: RecommendationTypeFilter;
  sort_by?: string;
  limit?: number;
  offset?: number;
  endpoints?: string[];
  expand?: boolean;
  strict?: boolean;
  ids?: string[];
};

export type Recommendations = {
  items: RecommendationFlow[];
  new_externals: ExternalHostType[];
  total_count?: number;
};

export type AcceptRecommendationsPayload = {
  items: {
    item: string;
    extra: string;
  }[];
};

export type DropdownMenuEntry = {
  id: string;
  name: string;
  color?: string;
};

export type FilterRecordDefault = {
  id: string;
  name: string;
};

export type NetworkMapFiltersType = {
  segmentFilter?: FilterRecordDefault[];
  subnetFilter?: FilterRecordDefault[];
  groupFilter?: FilterRecordDefault[];
  tagFilter?: FilterRecordDefault[];
  hostFilter?: FilterRecordDefault[];
  OSFilter?: FilterRecordDefault[];
  ifacesRangeFilter?: [number, number];
  rulesRangeFilter?: [number, number];
};

export type FlowHostInfo = {
  is_unknown?: boolean;
  hostname: string;
  ip?: string;
  ifname?: string;
  /** First domain name of an EXTERNAL endpoint, used in the card badge. */
  dns?: string;
  /** All CIDRs of an EXTERNAL endpoint; extras go in the badge title. */
  cidrs?: string[];
  /** All domain names of an EXTERNAL endpoint; extras go in the badge title. */
  dnsNames?: string[];
  /** A not-yet-created (`new_ext:`) EXTERNAL from a recommendation. */
  isDraft?: boolean;
  hostType: "AGENT" | "EXTERNAL" | "GROUP" | undefined;
};

export type OperationLogs = {
  id: string;
  dttm: string;
  user: string;
  user_login: string;
  user_email?: string;
  optype: string;
  op: string;
  data: string;
};

export type ConditionsFlows = {
  agent_id?: string;
  group_id?: string;
  external_id?: string;
  proto?: string;
  port?: number;
  tag?: string;
  page?: number;
  size?: number;
};

export const gradientColorSchemes = {
  networks: {
    color1: "#1E1F1F",
    color2: "#181B1B",
    color3: "#181B1B",
    color4: "#1E1F20",
    color5: "#121A1C",
  },
  auth: {
    color1: "#173A1F",
    color2: "#151E19",
    color3: "#151E19",
    color4: "#132017",
    color5: "#246541",
  },
  rules: {
    color1: "#0D2830",
    color2: "#111F22",
    color3: "#111F22",
    color4: "#12282B",
    color5: "#121A1C",
  },
  templates: {
    color1: "#30270D",
    color2: "#363009",
    color3: "#363009",
    color4: "#4D3311",
    color5: "#413D0C",
  },
  process: {
    color1: "#3A2E23",
    color2: "#332921",
    color3: "#332921",
    color4: "#201E1E",
    color5: "#362211",
  },
  alerts: {
    color1: "#49270A",
    color2: "#3A1212",
    color3: "#3A1212",
    color4: "#5B1F1F",
    color5: "#39291B",
  },
  "user-settings": {
    color1: "#0D2830",
    color2: "#111F22",
    color3: "#111F22",
    color4: "#12282B",
    color5: "#121A1C",
  },
  notifications: {
    color1: "#1A0D30",
    color2: "#030523",
    color3: "#030523",
    color4: "#132B5E",
    color5: "#1E2249",
  },
  "auth-providers": {
    color1: "#0D302D",
    color2: "#11221C",
    color3: "#11221C",
    color4: "#122B15",
    color5: "#121C17",
  },
};

export type GradientColorScheme = keyof typeof gradientColorSchemes;

export interface TabItem {
  id: number;
  name: string;
  current: boolean;
  pageTo?: RouteLocationRaw;
  pageLinkAriaLabel?: string;
}

export type Tabs = TabItem[];
export interface FuzzySearchRequest {
  q: string;
  Similarity: number;
  limit: number;
  offset: number;
  classes: UUIDClass[];
  domain?: string[];
}

export interface FuzzySearchItem {
  id: string;
  class: UUIDClass;
  str: string;
  domain: string;
  similarity: number;
}

export interface FuzzySearchResponse {
  items: FuzzySearchItem[];
}

export interface AiProposal {
  id: string;
  tool: string;
  arguments: string;
}

export interface AiChatToolCallDTO {
  id: string;
  type: string;
  name: string;
  arguments: string;
  proposal_status?: "pending" | "accepted" | "rejected" | "failed";
  proposal_error?: string;
  proposal_edited_arguments?: string;
  proposal_user_note?: string;
  proposal_snapshot?: string;
}

export interface AiChatMessageDTO {
  role: string;
  content: string;
  tool_calls?: AiChatToolCallDTO[];
  tool_call_id?: string;
  created_at?: string;
  seq?: number;
  image_refs?: string[];
}

export interface AiChatDTO {
  id: string;
  title: string;
  created_at?: string;
  updated_at?: string;
  messages?: AiChatMessageDTO[];
  compacted_upto_seq?: number;
}
